import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Heart, Coins, Clock, Award, ArrowLeft, Play, Sparkles, X } from "lucide-react";
import { API, useAuth } from "@/App";

export default function ContestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [myEntry, setMyEntry] = useState(null);
  const [myVotedEntry, setMyVotedEntry] = useState(null);
  const [showEnterModal, setShowEnterModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [res, spRes] = await Promise.all([
      fetch(`${API}/contests/${id}`),
      fetch(`${API}/contests/${id}/sponsorships`),
    ]);
    if (!res.ok) {
      toast.error("Contest not found");
      navigate("/contests");
      return;
    }
    setContest(await res.json());
    if (spRes.ok) {
      const sp = await spRes.json();
      setSponsors(sp.sponsorships || []);
    }
    if (user) {
      const me = await fetch(`${API}/contests/${id}/my-entry`, { credentials: "include" });
      if (me.ok) {
        const data = await me.json();
        setMyEntry(data.entry);
      }
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [id, user]);

  const openEnterModal = async () => {
    if (!user) {
      toast.error("Please log in to enter");
      navigate("/login");
      return;
    }
    if (!user.kyc_verified) {
      toast.error("KYC verification required to enter contests");
      navigate("/kyc");
      return;
    }
    // Fetch user's videos
    const res = await fetch(`${API}/videos?user_id=${user.user_id}&limit=20`, { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      setMyVideos(data.videos || []);
    }
    setShowEnterModal(true);
  };

  const submitEntry = async (videoId) => {
    const res = await fetch(`${API}/contests/${id}/enter`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ video_id: videoId }),
    });
    const data = await res.json();
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Failed to enter");
      return;
    }
    toast.success("You're in! Good luck 🍀");
    setShowEnterModal(false);
    refresh();
  };

  const vote = async (entryId) => {
    if (!user) {
      toast.error("Log in to vote");
      navigate("/login");
      return;
    }
    const res = await fetch(`${API}/contests/${id}/vote/${entryId}`, {
      method: "POST",
      credentials: "include",
    });
    const data = await res.json();
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Vote failed");
      return;
    }
    toast.success("Vote counted!");
    setMyVotedEntry(entryId);
    refresh();
  };

  if (loading) return <div className="min-h-screen bg-black text-white p-8">Loading…</div>;
  if (!contest) return null;

  const isLive = contest.status === "active";
  const isCompleted = contest.status === "completed";
  const ended = new Date(contest.end_at).getTime() < Date.now();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0014] via-[#1a0a2e] to-[#000] py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/contests")}
          className="text-pink-300 hover:text-pink-100 flex items-center gap-1 mb-4 text-sm font-bold"
          data-testid="contest-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> All contests
        </button>

        {/* Header */}
        <div className="rounded-lg border-4 border-black bg-gradient-to-br from-fuchsia-500 to-purple-700 p-6 shadow-[8px_8px_0_#000] mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-black text-yellow-300 px-2 py-1 text-[10px] font-black rounded uppercase">
              {contest.category}
            </span>
            {isLive && !ended && (
              <span className="bg-red-500 text-white px-2 py-1 text-[10px] font-black rounded animate-pulse">
                LIVE
              </span>
            )}
            {(isCompleted || ended) && (
              <span className="bg-gray-700 text-white px-2 py-1 text-[10px] font-black rounded">
                {isCompleted ? "WINNERS DECLARED" : "VOTING ENDED"}
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white leading-tight" data-testid="contest-title">
            {contest.title}
          </h1>
          <p className="text-yellow-100 text-lg font-bold mt-1">"{contest.theme}"</p>
          <p className="text-pink-100 text-sm mt-3 max-w-2xl">{contest.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-white text-xs font-bold">
            <Stat icon={<Trophy />} label="1st" value={`${contest.prize_first} 🪙`} />
            <Stat icon={<Award />} label="2nd" value={`${contest.prize_second} 🪙`} />
            <Stat icon={<Award />} label="3rd" value={`${contest.prize_third} 🪙`} />
            <Stat icon={<Clock />} label="Ends" value={new Date(contest.end_at).toLocaleDateString()} />
          </div>

          {isLive && !ended && (
            <div className="mt-5 flex flex-wrap gap-3">
              <Button
                onClick={openEnterModal}
                disabled={!!myEntry}
                className="bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300"
                data-testid="enter-contest-btn"
              >
                {myEntry ? "✓ You're entered!" : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Enter — {contest.entry_fee_coins > 0 ? `${contest.entry_fee_coins} coins` : "Free"}
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  if (!user) { navigate("/login"); return; }
                  if (!user.kyc_verified) { navigate("/kyc"); return; }
                  setShowSponsorModal(true);
                }}
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-black border-2 border-black hover:opacity-90"
                data-testid="sponsor-contest-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Sponsor this Contest
              </Button>
            </div>
          )}
        </div>

        {/* Sponsors bar */}
        {sponsors.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border-2 border-yellow-400 rounded-lg p-4 mb-6" data-testid="sponsors-bar">
            <p className="text-yellow-300 text-xs font-black uppercase mb-2">Proudly sponsored by</p>
            <div className="flex flex-wrap gap-4 items-center">
              {sponsors.map(sp => (
                <div key={sp.sponsorship_id} className={`flex items-center gap-2 px-3 py-1 rounded border-2 ${
                  sp.tier === "gold" ? "border-yellow-400 bg-yellow-400/20" :
                  sp.tier === "silver" ? "border-gray-300 bg-white/10" :
                  "border-orange-400 bg-orange-500/10"
                }`}>
                  {sp.brand_logo_url && (
                    <img src={sp.brand_logo_url} alt={sp.brand_name} className="w-6 h-6 rounded object-cover" />
                  )}
                  <a
                    href={sp.brand_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm font-black ${sp.tier === "gold" ? "text-yellow-200" : "text-white"} hover:underline`}
                  >
                    {sp.brand_name}
                  </a>
                  <span className="text-[10px] uppercase text-pink-200">{sp.tier_label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Winners (if completed) */}
        {isCompleted && contest.winners && contest.winners.length > 0 && (
          <div className="bg-yellow-400 border-4 border-black rounded-lg p-5 shadow-[6px_6px_0_#000] mb-6">
            <h2 className="text-2xl font-black text-black mb-1 flex items-center gap-2">
              <Trophy /> Winners
            </h2>
            {sponsors.find(s => s.tier === "gold") && (
              <p className="text-xs text-black/70 mb-3">
                Awarded by <strong>{sponsors.find(s => s.tier === "gold").brand_name}</strong>
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {contest.winners.map((w) => (
                <div key={w.entry_id} className="bg-white border-2 border-black p-3 rounded">
                  <div className="text-3xl mb-1">{w.rank === 1 ? "🥇" : w.rank === 2 ? "🥈" : "🥉"}</div>
                  <p className="font-black text-sm">{w.user_name}</p>
                  <p className="text-xs text-gray-600">Score: {w.score?.toFixed?.(1) ?? w.score}</p>
                  <p className="text-xs text-pink-600 font-bold">{w.coins_awarded} coins won</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Entries */}
        <h2 className="text-2xl font-black text-white mb-4">
          Entries ({contest.entries?.length ?? 0})
        </h2>
        {contest.entries?.length === 0 ? (
          <p className="text-pink-200/60 italic" data-testid="entries-empty">
            No entries yet. Be the first!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contest.entries?.map((e, idx) => (
              <EntryCard
                key={e.entry_id}
                entry={e}
                rank={idx + 1}
                canVote={isLive && !ended && user && user.user_id !== e.user_id}
                onVote={() => vote(e.entry_id)}
                voted={myVotedEntry === e.entry_id}
              />
            ))}
          </div>
        )}

        {/* Sponsor Modal */}
        {showSponsorModal && (
          <SponsorModal
            contestId={id}
            onClose={() => setShowSponsorModal(false)}
          />
        )}

        {/* Enter Modal */}
        {showEnterModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white border-4 border-black rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-[8px_8px_0_#FF1493]">
              <h2 className="text-2xl font-black mb-4">Pick a video to submit</h2>
              {myVideos.length === 0 ? (
                <p className="text-gray-700 mb-3">
                  You don't have any videos yet.{" "}
                  <button onClick={() => navigate("/feed")} className="text-pink-600 underline">
                    Upload one first.
                  </button>
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {myVideos.map((v) => (
                    <button
                      key={v.video_id}
                      onClick={() => submitEntry(v.video_id)}
                      className="border-2 border-black rounded overflow-hidden hover:shadow-[4px_4px_0_#FF1493] transition-all bg-gray-100"
                      data-testid={`pick-video-${v.video_id}`}
                    >
                      {v.thumbnail_url ? (
                        <img src={v.thumbnail_url} alt="" className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="w-full aspect-square bg-purple-200 flex items-center justify-center">
                          <Play className="w-8 h-8" />
                        </div>
                      )}
                      <p className="text-xs p-1 truncate">{v.caption || "Untitled"}</p>
                    </button>
                  ))}
                </div>
              )}
              <Button
                onClick={() => setShowEnterModal(false)}
                variant="outline"
                className="w-full border-2 border-black font-black"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-black/40 border-2 border-black/60 rounded p-2">
      <div className="text-pink-200 text-[10px] uppercase">{label}</div>
      <div className="text-white font-black">{value}</div>
    </div>
  );
}

function SponsorModal({ contestId, onClose }) {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("bronze");
  const [form, setForm] = useState({ brand_name: "", brand_url: "", brand_logo_url: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/sponsorship/tiers`).then(r => r.json()).then(d => setTiers(d.tiers || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_name.trim()) {
      toast.error("Brand name required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/sponsorships/create-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contest_id: contestId,
          tier: selectedTier,
          brand_name: form.brand_name,
          brand_url: form.brand_url || null,
          brand_logo_url: form.brand_logo_url || null,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="sponsor-modal">
      <form onSubmit={submit} className="bg-white text-black border-4 border-black rounded-lg shadow-[8px_8px_0_#F59E0B] w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-black">🎁 Sponsor this Contest</h2>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Promote your brand to contest viewers and winners. One-time payment, lasts for the full contest.
        </p>
        <div className="space-y-3 mb-4">
          {tiers.map(t => (
            <label
              key={t.tier_id}
              className={`block p-3 rounded border-2 cursor-pointer transition ${
                selectedTier === t.tier_id ? "border-amber-500 bg-amber-50" : "border-black bg-white hover:bg-gray-50"
              }`}
              data-testid={`tier-${t.tier_id}`}
            >
              <input type="radio" name="tier" value={t.tier_id} checked={selectedTier === t.tier_id} onChange={() => setSelectedTier(t.tier_id)} className="mr-2" />
              <span className="font-black">{t.label}</span>
              <span className="ml-2 text-amber-600 font-bold">${t.amount}</span>
              <ul className="text-xs text-gray-700 mt-1 ml-6 list-disc">
                {t.perks.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </label>
          ))}
        </div>
        <div className="space-y-2 mb-4">
          <div>
            <Label>Brand name *</Label>
            <Input required value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} data-testid="sponsor-brand-name" />
          </div>
          <div>
            <Label>Website URL (optional)</Label>
            <Input type="url" placeholder="https://" value={form.brand_url} onChange={e => setForm({ ...form, brand_url: e.target.value })} />
          </div>
          <div>
            <Label>Logo image URL (optional)</Label>
            <Input type="url" placeholder="https://..." value={form.brand_logo_url} onChange={e => setForm({ ...form, brand_logo_url: e.target.value })} />
          </div>
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-amber-500 hover:bg-amber-400 text-white font-black border-2 border-black"
          data-testid="sponsor-submit-btn"
        >
          {submitting ? "Creating checkout…" : "Continue to Payment"}
        </Button>
      </form>
    </div>
  );
}

function EntryCard({ entry, rank, canVote, onVote, voted }) {
  return (
    <div className="rounded-lg border-2 border-black bg-white shadow-[4px_4px_0_#000] overflow-hidden" data-testid={`entry-${entry.entry_id}`}>
      <div className="aspect-video bg-black relative">
        {entry.video_url ? (
          <video src={entry.video_url} className="w-full h-full object-cover" controls preload="metadata" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white">
            <Play />
          </div>
        )}
        <div className="absolute top-2 left-2 bg-yellow-400 text-black font-black text-xs px-2 py-1 rounded border border-black">
          #{rank}
        </div>
      </div>
      <div className="p-3">
        <p className="font-bold text-sm">{entry.user_name}</p>
        <p className="text-xs text-gray-600 line-clamp-2 mb-2">{entry.video_caption}</p>
        <div className="flex items-center justify-between text-xs">
          <div className="flex gap-2 text-gray-700">
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {entry.score_likes}</span>
            <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> {entry.score_votes}</span>
          </div>
          {canVote && (
            <Button
              size="sm"
              onClick={onVote}
              disabled={voted}
              className="bg-pink-500 hover:bg-pink-400 text-white text-xs h-7 border border-black"
              data-testid={`vote-btn-${entry.entry_id}`}
            >
              {voted ? "✓ Voted" : "Vote"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
